import { Modal, Button, message as antMessage, Space } from 'antd'
import { ExclamationCircleOutlined, CopyOutlined, MessageOutlined } from '@ant-design/icons'
import type { ErrorContext } from '../types/feedback.types'

/**
 * Error details from backend or application
 */
export interface ErrorDetail {
  code: string
  message: string
  details?: any
}

/**
 * Callback type for opening feedback modal
 */
export type OpenFeedbackCallback = (errorContext: ErrorContext) => void

/**
 * ErrorService - Universal Error Display
 *
 * All errors in the application flow through this service for consistent UX.
 * Displays errors via Ant Design Modal.error() with:
 * - Persistent modals (user must close)
 * - Copyable error text (for support tickets)
 * - "Report to Rocket Pricing" button (opens feedback modal)
 * - Consistent formatting
 *
 * Integration:
 * - apiService: Automatically calls showError() on API failures
 * - ErrorBoundary: Catches React crashes and displays via showError()
 * - Components: Call directly for validation/business errors
 * - FeedbackModal: Registered via setFeedbackCallback() to enable reporting
 */
class ErrorService {
  private feedbackCallback: OpenFeedbackCallback | null = null

  /**
   * Register callback for opening feedback modal
   * Called by App.tsx during initialization
   *
   * @param callback - Function to open feedback modal with error context
   */
  setFeedbackCallback(callback: OpenFeedbackCallback): void {
    this.feedbackCallback = callback
  }

  /**
   * Temporarily disable feedback callback (e.g., during feedback submission)
   * Returns a function to re-enable it
   */
  disableFeedbackCallback(): () => void {
    const savedCallback = this.feedbackCallback
    this.feedbackCallback = null
    return () => {
      this.feedbackCallback = savedCallback
    }
  }

  /**
   * Display error in persistent modal with copy functionality
   *
   * @param title - Modal title (e.g., "Failed to Load Offerings")
   * @param message - Primary error message
   * @param errors - Optional array of detailed error objects
   */
  showError(
    title: string,
    message: string,
    errors?: ErrorDetail[]
  ): void {
    const errorText = this.formatErrorText(title, message, errors)
    const errorCode = errors && errors.length > 0 ? errors[0].code : 'UNKNOWN_ERROR'
    const errorStack = errors && errors.length > 0 && errors[0].details?.stack
      ? errors[0].details.stack
      : undefined
    const eventId = 'error-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)

    Modal.error({
      title,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>{message}</p>
          {errors && errors.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <strong>Error Details:</strong>
              <div style={{
                marginTop: '8px',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '8px',
                backgroundColor: 'var(--rp-surface-muted)',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace'
              }}>
                {errors.map((error, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <div>
                      <strong>{error.code}</strong>: {error.message}
                    </div>
                    {error.details && (
                      <pre style={{
                        margin: '4px 0 0 0',
                        fontSize: '11px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {JSON.stringify(error.details, null, 2)}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{
            padding: '8px',
            backgroundColor: 'var(--rp-surface-tertiary)',
            borderRadius: '4px',
            fontSize: '12px',
            color: 'var(--rp-gray-700)'
          }}>
            <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>
              Copy error details for support:
            </div>
            <textarea
              readOnly
              value={errorText}
              style={{
                width: '100%',
                minHeight: '160px',
                padding: '8px',
                fontSize: '11px',
                fontFamily: 'monospace',
                border: '1px solid var(--rp-border-subtle)',
                borderRadius: '4px',
                resize: 'vertical'
              }}
              placeholder="Error details"
            />
            <Space style={{ marginTop: '8px', width: '100%', justifyContent: 'space-between' }}>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(errorText)
                    .then(() => {
                      antMessage.success('Error details copied to clipboard')
                    })
                    .catch((err) => {
                      console.error('Failed to copy to clipboard:', err)
                      antMessage.error('Failed to copy to clipboard')
                    })
                }}
              >
                Copy
              </Button>
              {this.feedbackCallback && (
                <Button
                  type="default"
                  icon={<MessageOutlined />}
                  onClick={() => {
                    Modal.destroyAll()
                    this.feedbackCallback!({
                      errorCode,
                      errorMessage: message,
                      errorStack,
                      eventId,
                      pageUrl: window.location.href,
                      browserInfo: navigator.userAgent
                    })
                  }}
                >
                  Report to Rocket Pricing
                </Button>
              )}
            </Space>
          </div>
        </div>
      ),
      okText: 'Close',
      width: 600,
      maskClosable: false,
    })

    console.error('ErrorService:', {
      title,
      message,
      errors,
      eventId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    })
  }

  private formatErrorText(
    title: string,
    message: string,
    errors?: ErrorDetail[]
  ): string {
    let text = 'ERROR: ' + title + '\n\n'
    text += 'Message: ' + message + '\n\n'
    if (errors && errors.length > 0) {
      text += 'Details:\n'
      errors.forEach((error, index) => {
        text += '\n' + (index + 1) + '. ' + error.code + ': ' + error.message + '\n'
        if (error.details) {
          text += '   ' + JSON.stringify(error.details, null, 2) + '\n'
        }
      })
      text += '\n'
    }
    text += 'Timestamp: ' + new Date().toISOString() + '\n'
    text += 'URL: ' + window.location.href + '\n'
    text += 'User Agent: ' + navigator.userAgent + '\n'
    return text
  }
}

export const errorService = new ErrorService()
export default errorService
